import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-xl bg-background-900 border border-background-800/60 hover:border-background-700/60 transition-all duration-300"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/40 to-transparent"></div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            {article.isExclusive && (
              <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500/20 text-accent-400 border border-accent-500/30">独家</span>
            )}
            {article.isHot && (
              <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-primary-500/20 text-primary-400 border border-primary-500/30">热门</span>
            )}
            <span className="text-foreground-500 text-xs">{article.categoryName}</span>
          </div>
          <h3 className="text-lg font-bold text-foreground-50 leading-snug mb-2 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2" style={{ fontFamily: "var(--font-heading)" }}>
            {article.title}
          </h3>
          <p className="text-foreground-500 text-sm line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-600">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views.toLocaleString()}
            </span>
            <span>{article.publishedAt}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-800/30 transition-colors duration-200"
      >
        <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-background-800">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground-200 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-600">
            <span>{article.categoryName}</span>
            <span>·</span>
            <span>{article.publishedAt}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="group block bg-background-900 rounded-xl border border-background-800/60 hover:border-background-700/60 overflow-hidden transition-all duration-300"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-background-800 text-foreground-400">
            {article.categoryName}
          </span>
          {article.isExclusive && (
            <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500/20 text-accent-400 border border-accent-500/30">独家</span>
          )}
        </div>
        <h3 className="text-base font-bold text-foreground-100 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2 leading-snug mb-2" style={{ fontFamily: "var(--font-heading)" }}>
          {article.title}
        </h3>
        <p className="text-foreground-500 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-600">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.views.toLocaleString()} 阅读
          </span>
          <span>{article.publishedAt}</span>
        </div>
      </div>
    </Link>
  );
}